<template>
  <div
    v-if="state"
    class="san-refinement-list"
    :class="[items.length === 0 && 'no-refinement']"
  >
    <slot
      :items="items"
      :refine="refine"
      :search-for-items="state.searchForItems"
      :search-for-items-query="searchForFacetValuesQuery"
      :toggle-show-more="toggleShowMore"
      :can-toggle-show-more="state.canToggleShowMore"
      :is-showing-more="state.isShowingMore"
      :create-url="state.createUrl"
      :is-from-search="state.isFromSearch"
      :can-refine="state.canRefine"
      :send-event="state.sendEvent"
    >
      <div
        v-if="searchable"
        class="san-refinement-list--searchbox"
      >
        <SearchInput
          v-model="searchForFacetValues"
          :show-loading-indicator="true"
          :placeholder="searchablePlaceholder"
        />
      </div>
      <slot
        v-if="state.isFromSearch && items.length === 0"
        name="noResults"
        :query="searchForFacetValues"
      >
        <div class="san-refinement-list--no-results">
          No results.
        </div>
      </slot>
      <ul
        v-if="items.length > 0"
        class="san-refinement-list--list"
      >
        <li
          v-for="item in items"
          :key="item.value"
          class="san-refinement-list--item"
          :class="[item.isRefined && 'san-refinement-list--item--is-selected']"
        >
          <slot
            name="item"
            :item="item"
            :refine="refine"
            :create-u-r-l="state.createUrl"
          >
            <label class="san-refinement-list--label">
              <input
                class="san-refinement-list--checkbox"
                type="checkbox"
                :value="item.value"
                :checked="item.isRefined"
                @change="refine(item.value)"
              >
              <span
                v-if="searchable"
                class="san-refinement-list--label-text"
              >
                <ais-highlight
                  attribute="item"
                  :hit="item"
                />
              </span>
              <span
                v-else
                class="san-refinement-list--label-text"
              >{{ item.label }}</span>
              <span class="san-refinement-list--count">{{ item.count }}</span>
            </label>
          </slot>
        </li>
      </ul>
      <button
        v-if="showMore"
        class="san-refinement-list--show-more"
        :class="[!state.canToggleShowMore && 'san-refinement-list--show-more--disabled']"
        :disabled="!state.canToggleShowMore"
        @click="toggleShowMore"
      >
        <slot
          name="showMoreLabel"
          :is-showing-more="state.isShowingMore"
        >
          Show {{ state.isShowingMore ? 'less' : 'more' }}
        </slot>
      </button>
    </slot>
  </div>
</template>

<script setup>
import { inject, ref, computed, watch } from 'vue'
import { useConnector } from '@/composables'
import { connectRefinementList } from 'instantsearch.js/es/connectors'
import SearchInput from 'vue-instantsearch/src/components/SearchInput.vue'

defineOptions({ name: 'SanRefinementList' })
const props = defineProps({
  attribute: { type: String, required: true },
  searchable: { type: Boolean, default: undefined },
  searchablePlaceholder: { type: String, required: false, default: 'Search here…' },
  operator: { default: 'or', validator (value) {
    return value === 'and' || value === 'or'
  }, required: false },
  limit: { type: Number, required: false, default: 999999 },
  showMoreLimit: { type: Number, required: false, default: 999999 },
  showMore: { type: Boolean, required: false, default: false },
  sortBy: { type: [Array, Function], required: false, default: undefined },
  transformItems: { type: Function, required: false, default: undefined }
})

const $emitter = inject('instantSearchPanelEmitter', { emit: () => {} })

const noop = () => {}

const state = useConnector(connectRefinementList, {
  attribute: props.attribute,
  operator: props.operator,
  limit: props.limit,
  showMore: props.showMore,
  showMoreLimit: props.showMoreLimit,
  sortBy: props.sortBy,
  escapeFacetValues: true,
  transformItems: props.transformItems
})

const searchForFacetValuesQuery = ref('')
const hasEmitted = ref(false)

const searchForFacetValues = computed({
  get: () => {
    return searchForFacetValuesQuery.value
  },
  set: value => {
    state.value.searchForItems(value)
    searchForFacetValuesQuery.value = value
  }
})

const toggleShowMore = computed(() => {
  return state.value.toggleShowMore || noop
})

const items = computed(() => {
  return state.value.items.map(item =>
    Object.assign({}, item, {
      _highlightResult: {
        item: {
          value: item.highlighted
        }
      }
    })
  )
})

const refine = value => {
  state.value.refine(value)
  searchForFacetValuesQuery.value = ''
}

const mapStateToCanRefine = s => !!s.canRefine

watch(() => state.value, (newState, oldState) => {
  if (!newState) return

  const previousCanRefine = mapStateToCanRefine(oldState || {})
  const nextCanRefine = mapStateToCanRefine(newState)

  if (!hasEmitted.value || previousCanRefine !== nextCanRefine) {
    $emitter.emit('PANEL_CHANGE_EVENT', nextCanRefine)
    hasEmitted.value = true
  }
}, { immediate: true })
</script>
